import React, { useEffect, useRef, useState } from 'react'
import AddImage from '../../../assets/ai-tutor/addImage.svg'
import Upload from '../../../assets/ai-tutor/upload.svg';
import Error from '../../../assets/ai-tutor/error.svg';
import DeleteImage from '../../../assets/ai-tutor/deleteImage.svg'
import { PlainInputStyles } from './PlainInputStyles';
import pkg from "@inrscr/coschool-ui-components";
import {
    Box, Image, Text, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
    ModalCloseButton, Grid, HStack, Center, Tooltip, VStack, Spinner,
    Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, PopoverFooter,
    PopoverArrow, PopoverCloseButton, PopoverAnchor,
    UnorderedList,
    ListItem,
} from "@chakra-ui/react";
import { fileUpload } from '../../../network/UploadService';
import { isEmptyOrNull } from '../../../utils/common-utils';
import { fetchImageToText } from '../../../network/TutorApiService';
import { RxPlus } from "react-icons/rx";
import { CiEdit } from "react-icons/ci";
import { IoMdCheckmark } from "react-icons/io";
import useStateRef from 'react-usestateref';
import { styles } from './ImageTranscriptionStyles';
import * as ReactLoaderSpinner from 'react-loader-spinner';
import { useTutor } from '../../../context/AiTutorContext';
import { RiFormula } from "react-icons/ri";
import { FaInfoCircle } from "react-icons/fa";
import { useRoot } from '../../../context/RootContext';
import { handleExtractSolution } from '../../../utils/TutorConstants';
import KatexRender from '../../../components/molecules/ai-tutor/KatexRender';

const { ThreeDots } = ReactLoaderSpinner;
const STATUS = {
    IDLE: '',
    PENDING: 'pending',
    UPLOADING: 'uploading',
    UPLOAD_FAILED: 'upload_failed',
    CONVERT_FAILED: 'convert_failed',
    SUCCESS: 'success'
};

const initialState = { image: "", text: "", status: STATUS?.IDLE, otherMetadata: {}, ocrStatus: true }

export default function ImageTranscription({ onClick, chatId, currentQues }) {
    const { CustomButton, LatexRenderView, StudentEditor } = pkg;
    const { getCurriculumInfo } = useTutor();
    const { isPlayground, playgroundModel, isStandalone } = useRoot();
    const fileInputRef = useRef(null);
    const [openImageModal, setOpenImageModal] = useState(false);
    const [imageList, setImageList, imageListRef] = useStateRef([]);
    const [isProcessing, setIsProcessing, isProcessingRef] = useStateRef(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [inputvalue, setInputValue] = useState("");
    const [openMath, setOpenMath] = useState(false);
    const [seqNumber, setSeqNumber, seqNumberRef] = useStateRef(0);
    const [hiecConv, setHiecConv] = useState(null);
    const [hiec2any, setHiec2any] = useState(null);
    const [currentItem, setCurrentItem, currentItemRef] = useStateRef(initialState);

    // Load both heic2any and heic-convert in useEffect
    useEffect(() => {
        const loadConverters = async () => {
            try {
                // Load both libraries
                const heic2anyModule = await import('heic2any');
                const heicConvertModule = await import('heic-convert/browser');

                // Store both converters
                setHiec2any(() => heic2anyModule.default);
                setHiecConv(() => heicConvertModule.default);

                console.log("HEIC conversion libraries loaded successfully");
            } catch (err) {
                console.error('Error importing HEIC conversion libraries:', err);
            }
        };
        openImageModal && loadConverters();
    }, [openImageModal]);


    const handleOpenModal = () => setOpenImageModal(true);
    const handleCloseModal = () => setOpenImageModal(false);

    const handleButtonClick = () => {
        setSeqNumber(0);
        setImageList([]);
        setCurrentItem(initialState);
        handleOpenModal();
        setIsEditMode(false);
        handleResetEdit();
    };
    const handleFileChange = async (event) => {
        setSeqNumber((prev) => prev + 1);
        let newFiles = Array.from(event.target.files);
        event.target.value = "";

        const MAX_FILE_SIZE = parseInt(window?.ENV?.OCR_UPLOAD_SIZE_LIMIT) * 1024 * 1024;
        const validFiles = newFiles.filter(file => file.size <= MAX_FILE_SIZE);
        // Rename files using UUID
        newFiles = validFiles.map(file => {
            const extension = file?.name?.substring(file?.name?.lastIndexOf('.'));
            const newName = `${chatId + '-' + seqNumberRef?.current}${extension}`;
            return new File([file], newName, { type: file.type });
        });
        if (validFiles.length === 0) {
            alert(`Please select files that are less than ${window?.ENV?.OCR_UPLOAD_SIZE_LIMIT}MB.`);
            return;
        }

        // Check for file name length (max 255 characters)
        if (validFiles[0]?.name?.length > 255) {
            alert("File name is too long.");
            return;
        }
        setIsProcessing(true);
        setCurrentItem(initialState);
        // Create a preview URL for the first file
        const file = newFiles[0];
        // In your handleFileChange function, update to use both libraries
        if (file?.type?.includes("heic") || file?.type?.includes("heif")
            || file?.name?.toLowerCase()?.endsWith('.heic') || file?.name?.toLowerCase()?.endsWith('.heif')) {
            try {
                console.log("Attempting HEIC file conversion...");

                // Get the file as ArrayBuffer
                const fileBuffer = await validFiles[0].arrayBuffer();

                try {
                    // First try with heic2any
                    const jpegBlob = await hiec2any({
                        blob: new Blob([fileBuffer], { type: file.type }),
                        toType: "image/jpeg",
                        quality: 0.9
                    });

                    // Create a new File from the blob
                    const convertedFile = new File(
                        [jpegBlob],
                        newFiles[0].name.replace(/\.heic$|\.heif$/i, '.jpeg'),
                        { type: 'image/jpeg' }
                    );
                    // Replace the HEIC file with the converted JPEG
                    newFiles = [convertedFile];
                    // Create preview URL
                    const previewURL = URL.createObjectURL(jpegBlob);
                    setCurrentItem((prev) => ({ ...prev, image: previewURL }));
                } catch (heic2anyError) {
                    console.error("heic2any conversion error:", heic2anyError);

                    // Handle "already browser readable" error
                    if (heic2anyError?.message?.includes("already browser readable")) {
                        console.log("Image is already in a readable format, displaying directly");
                        const previewURL = URL.createObjectURL(file);
                        setCurrentItem((prev) => ({ ...prev, image: previewURL }));
                        // Keep the file as is since it's already in a readable format
                        const newFile = new File(
                            [file],
                            newFiles[0].name.replace(/\.heic$|\.heif$/i, '.jpeg'),
                            { type: 'image/jpeg' }
                        );
                        newFiles = [newFile];
                    }
                    // When format not supported by heic2any, try heic-convert as fallback
                    else if (heic2anyError?.message?.includes("format not supported") ||
                        heic2anyError?.code === 2) {
                        console.log("Format not supported by heic2any, trying heic-convert as fallback");

                        try {
                            // Use heic-convert with browser-specific approach
                            const jpegBuffer = await hiecConv({
                                buffer: new Uint8Array(fileBuffer),
                                format: 'JPEG',      // output format
                                quality: 0.9        // the jpeg compression quality
                            });

                            // Convert the buffer to a blob
                            const jpegBlob = new Blob([jpegBuffer], { type: 'image/jpeg' });

                            // Create a new File from the blob
                            const convertedFile = new File(
                                [jpegBlob],
                                newFiles[0].name.replace(/\.heic$|\.heif$/i, '.jpeg'),
                                { type: 'image/jpeg' }
                            );

                            // Replace the HEIC file with the converted JPEG
                            newFiles = [convertedFile];

                            // Create preview URL
                            const previewURL = URL.createObjectURL(jpegBlob);
                            setCurrentItem((prev) => ({ ...prev, image: previewURL }));
                        } catch (heicConvertError) {
                            console.error("heic-convert fallback also failed:", heicConvertError);
                            throw new Error("HEIC conversion failed with both libraries");
                        }
                    }
                    else {
                        // For other errors, rethrow to be caught by the outer catch
                        throw heic2anyError;
                    }
                }
            } catch (error) {
                console.error("Error during HEIC handling:", error);

                // Final fallback - try direct browser display
                try {
                    const previewURL = URL.createObjectURL(file);
                    setCurrentItem((prev) => ({ ...prev, image: previewURL }));
                } catch (fallbackError) {
                    // If everything fails, show upload failed
                    console.error("All HEIC handling methods failed:", fallbackError);
                    setIsProcessing(false);
                    setCurrentItem((prev) => ({ ...prev, status: STATUS?.UPLOAD_FAILED }));
                }
            }
        } else {
            // For non-HEIC files, proceed normally
            const previewURL = URL.createObjectURL(file);
            setCurrentItem((prev) => ({ ...prev, image: previewURL }));
        }


        let response = await fileUpload(newFiles, "student-handwritten");
        const image = response?.[0]?.originalURL;
        const mimeType = response?.[0]?.mimeType;
        if (!isEmptyOrNull(image)) {
            setCurrentItem((prev) => ({ ...prev, image: image, status: STATUS?.PENDING }));
            setImageList([...imageListRef?.current, { image, text: "", status: STATUS.PENDING }]);
            handleImageToText(image, mimeType);
        } else {
            setIsProcessing(false);
            setCurrentItem((prev) => ({ ...prev, status: STATUS?.UPLOAD_FAILED }));
        }
    };

    const handleImageToText = async (image, mime_type) => {
        const question = currentQues?.question_layout;
        const solution = handleExtractSolution(currentQues);
        const { grade_id, subject_id, topic_name_id, grade, subject, topic_name } = getCurriculumInfo();
        const body = {
            mime_type, grade, subject, topic_name, grade_id, subject_id,
            topic_name_id, image_urls: [image], question, solution,
            solution_layout: question?.solution_layout
        }
        if (isPlayground) {
            body["playground_flag"] = true;
            body["model"] = playgroundModel;
        }
        if (isEmptyOrNull(body?.model)) {
            body["model"] = "moe"; //default model
        }
        setIsProcessing(true);
        setCurrentItem((prev) => ({ ...prev, status: STATUS?.PENDING }));
        const apiResponse = await fetchImageToText(chatId, body);
        const response = apiResponse?.data || {}
        setIsProcessing(false);
        const ocrStatus = response?.image_quality?.proceed_with_ocr || false;
        if (response?.status == "success") {
            if (currentItemRef?.current?.image == image) {
                setCurrentItem((prev) => ({
                    ...prev,
                    text: response?.transcript,
                    otherMetadata: response?.image_quality,
                    status: STATUS?.SUCCESS,
                    ocrStatus: ocrStatus
                }));
            } else {
                const data = imageListRef?.current?.find((item) => item?.image == currentItemRef?.current?.image);
                setCurrentItem((prev) => ({ ...prev, text: data?.text }));
            }
            let existing = [...imageListRef?.current];
            existing = existing?.map((item) => {
                return item?.image == image ?
                    {
                        ...item,
                        text: response?.transcript,
                        otherMetadata: response?.image_quality,
                        status: STATUS?.SUCCESS,
                        ocrStatus: ocrStatus
                    }
                    :
                    item;
            })
            setImageList(existing);
        } else {
            if (currentItemRef?.current?.image == image) {
                setCurrentItem((prev) => ({ ...prev, status: STATUS?.CONVERT_FAILED, ocrStatus: ocrStatus }));
            }
            let existing = [...imageListRef?.current];
            existing = existing?.map((item) => {
                return item?.image == image ?
                    { ...item, text: "", status: STATUS?.CONVERT_FAILED, ocrStatus: ocrStatus }
                    : item;
            })
            setImageList(existing);
        }
        return;
    }

    const handleAddImage = () => {
        handleResetEdit();
        fileInputRef.current.click();
    }
    const handleUpdateText = () => {
        if (isEditMode) {
            let findIndex = imageListRef?.current?.findIndex((item) => item?.image == currentItemRef?.current?.image);
            let existing = [...imageListRef?.current];
            existing[findIndex] = { ...existing[findIndex], text: inputvalue };
            setImageList(existing);
            setCurrentItem((prev) => ({ ...prev, text: inputvalue }));
            handleResetEdit();
        } else {
            setIsEditMode(true);
            setInputValue(currentItemRef?.current?.text);
        }
    }

    const handleResetEdit = () => {
        setIsEditMode(false)
        setInputValue("")
    }
    const handleDeleteImage = (image) => {
        handleResetEdit();
        let existing = [...imageListRef?.current || []];
        existing = existing?.filter((item) => item.image != image);
        setImageList(existing);
        if (existing?.length > 0) {
            const prevItem = existing?.[existing?.length - 1];
            setCurrentItem((prev) => ({
                ...prev,
                image: prevItem?.image,
                text: prevItem?.text,
                status: prevItem?.status,
                otherMetadata: prevItem?.otherMetadata,
                ocrStatus: prevItem?.ocrStatus
            }));
        } else {
            setCurrentItem((prev) => ({ ...prev, image: "", text: "", status: STATUS?.IDLE, ocrStatus: true }));
        }
    }

    const handleSelect = (item) => {
        handleResetEdit();
        setCurrentItem((prev) => ({
            ...prev,
            image: item?.image,
            text: item?.text,
            status: item?.status,
            otherMetadata: item?.otherMetadata,
            ocrStatus: item?.ocrStatus

        }));

    }
    const bottomButtons = () => {
        const totalText = imageList?.map((item) => item?.text).join("");
        const hasPendingOrUploading = imageList?.some((item) => item?.status === STATUS?.PENDING || item?.status === STATUS?.UPLOADING);
        const hasAnyOcrStatusFalse = imageList?.some((item) => item?.ocrStatus === false);
        return (
            <VStack w="100%" alignItems={"stretch"}>
                <HStack w="100%">
                    {imageList?.length > 0 && (
                        <HStack flexWrap="wrap">
                            {imageList?.map((item, index) => {
                                const isSelected = currentItemRef?.current?.image === item?.image;
                                const isOcrFailed = item?.ocrStatus == false;
                                return (
                                    <Box sx={isOcrFailed ? { border: "2px solid #E52C2C", position: "relative" } : {}}>
                                        <Image
                                            onClick={() => { handleSelect(item) }}
                                            key={index}
                                            src={item?.image}
                                            sx={{ ...styles.imageListThumbnail(isSelected, isOcrFailed) }}
                                            loading='lazy'
                                        />
                                        {isOcrFailed &&
                                            <VStack w="100%" bg=" #FFE4E4" position={"absolute"} bottom={"0px"}>
                                                <Text fontSize="12px" as="span" color="#E52C2C">Error</Text>
                                            </VStack>
                                        }
                                    </Box>
                                );
                            })}
                        </HStack>
                    )}
                </HStack>
                <HStack justifyContent={"flex-end"} w="100%">
                    <CustomButton
                        label="Cancel"
                        isDisabled={isProcessingRef?.current}
                        handleClick={() => {
                            handleCloseModal();
                        }}
                        customStyles={styles.cancelButton}
                    />
                    <CustomButton
                        label="Submit"
                        isDisabled={isEditMode || isEmptyOrNull(totalText?.trim()) || hasPendingOrUploading || hasAnyOcrStatusFalse}
                        handleClick={() => {
                            const text = imageList?.map((item) => item?.text).join(" \n\n ");
                            onClick(text, imageList);
                            handleCloseModal();
                        }}
                        customStyles={PlainInputStyles()?.backToPreviewCustomStyles}
                    />
                </HStack>
            </VStack>
        )
    }

    const renderStatusContent = () => {
        const { status: currentStatus, ocrStatus = true } = currentItemRef?.current || {};
        switch (currentStatus) {
            case STATUS.PENDING:
                return (
                    <React.Fragment>
                        <Text color={"#000000D9"} fontSize={"16px"} fontWeight={"400"} mb={2}>
                            Transcribing the contents of the file...
                        </Text>
                        <ThreeDots
                            visible={true} height="60" width="60" color="#1C9E4E" radius="9" ariaLabel="three-dots-loading"
                            wrapperStyle={{ marginLeft: "20px" }}
                            wrapperClass=""
                        />
                    </React.Fragment>
                );
            case STATUS.CONVERT_FAILED:
                return (
                    <React.Fragment>
                        {ocrStatus ?
                            <Text color="#000000D9" fontSize="16px" fontWeight="400" mb={2}>
                                Transcription failed. Please try again...
                            </Text>
                            :
                            <Box>
                                <HStack alignItems={"flex-start"}>
                                    <Image src={Error} alt="error image" />
                                    <VStack align={"stretch"}>
                                        <Text as="div" fontWeight={"600"} fontSize={["14px", "16px"]}>Image transcription rejected</Text>
                                        <Text as="div" fontSize={["14px", "16px"]}>
                                            Transcription was not performed because one or more of the following issues were detected:</Text>
                                        <UnorderedList fontSize={["12px", "14px"]}>
                                            <ListItem>The image contains printed text instead of handwriting</ListItem>
                                            <ListItem>The handwriting is illegible and cannot be reliably interpreted</ListItem>
                                            <ListItem>The image is rotated or skewed, making it difficult to read</ListItem>
                                        </UnorderedList>
                                    </VStack>
                                </HStack>

                            </Box>
                        }
                    </React.Fragment>
                );
            case STATUS.UPLOADING:
                return (
                    <React.Fragment>
                        <Text color={"#000000D9"} fontSize={"16px"} fontWeight={"400"} mb={2}>
                            Waiting for file to be uploaded...
                        </Text>
                        <ThreeDots
                            visible={true} height="60" width="60" color="#1C9E4E" radius="9" ariaLabel="three-dots-loading"
                            wrapperStyle={{ marginLeft: "20px" }}
                            wrapperClass=""
                        />
                    </React.Fragment>
                );
            default:
                return null;
        }
    };
    const renderMetadata = (otherMetadata) => {
        const { clarity, handwriting_legibility, issues, orientation, quality_score } = otherMetadata || {};
        const metadata = [
            { label: "Clarity", value: clarity },
            { label: "Handwriting Legibility", value: handwriting_legibility },
            { label: "Issues", value: issues?.join(", ") },
            { label: "Orientation", value: orientation },
            { label: "Quality Score", value: quality_score }
        ]
        return (
            <VStack alignItems={"flex-start"}>
                {metadata?.map((item, index) => {
                    return (
                        <React.Fragment>
                            <Text fontSize={"14px"} as={"div"} key={index}>
                                {`${item?.label} : `}
                                <span style={{ fontWeight: "bold", paddingLeft: "2px" }}>{item?.value}</span>
                            </Text>
                        </React.Fragment>
                    )
                })}
            </VStack>
        )
    }

    const { text: currentText, image: currentImage, status: currentStatus, otherMetadata = {} } = currentItemRef?.current || {};
    return (
        <React.Fragment>
            <Tooltip hasArrow label='Upload file' bg='#212121' color='#FFFFFF'
                borderRadius={"4px"} fontSize={"14px"}
            >
                <Image onClick={handleButtonClick} src={AddImage} w={"28px"} h={"28px"}
                    cursor={"pointer"}
                />
            </Tooltip>

            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"  // Restricts to image files
                onChange={handleFileChange}
            />
            <Modal isOpen={openImageModal} onClose={handleCloseModal} isCentered={true} {...styles.modal}>
                <ModalOverlay />
                <ModalContent {...styles.modalContent}>
                    <ModalCloseButton />
                    <ModalHeader {...styles.modalHeader}>{"Upload file"}</ModalHeader>
                    <ModalBody p={"8px"}>
                        <Box sx={{ h: "auto", minH: "400px" }} mb={2}>
                            {(imageList?.length > 0 || !isEmptyOrNull(currentImage) || currentStatus != STATUS?.IDLE) ? (
                                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} h={{ base: "auto", md: "400px" }} p={'4px'}>
                                    <Box border={"1px solid #00000029"} position="relative">
                                        {currentStatus == STATUS?.UPLOADING && isEmptyOrNull(currentImage) &&
                                            <Center h="100%">
                                                <VStack>
                                                    <Spinner size="xl" color="#5F4DC7" />
                                                    <Text as="div">Uploading your file...</Text>
                                                </VStack>
                                            </Center>
                                        }
                                        {currentStatus == STATUS?.UPLOAD_FAILED &&
                                            <Center h="100%">
                                                <Text as="div">Failed to load file</Text>
                                            </Center>
                                        }

                                        {currentStatus == STATUS.SUCCESS && Object.keys(otherMetadata)?.length > 0 &&
                                            <Box {...styles.rightTopControls}>
                                                <Popover>
                                                    <PopoverTrigger>
                                                        <Center bg={"#F5F5F5"} p={'4px'} cursor={"pointer"}>
                                                            <FaInfoCircle size={"16px"} color='#5F4DC7' />
                                                        </Center>
                                                    </PopoverTrigger>
                                                    <PopoverContent>
                                                        <PopoverArrow />
                                                        <PopoverCloseButton />
                                                        <PopoverBody>
                                                            {renderMetadata(otherMetadata)}
                                                        </PopoverBody>
                                                    </PopoverContent>
                                                </Popover>
                                            </Box>
                                        }
                                        {!isEmptyOrNull(currentImage) && (
                                            <React.Fragment>
                                                <Image
                                                    loading='lazy'
                                                    src={currentImage}
                                                    w="100%"
                                                    height={["240px", "400px"]}
                                                />
                                                <Box {...styles.leftControls}>
                                                    <Center bg={"#F5F5F5"} p={2} cursor={"pointer"} color={"#DF5959"}
                                                        onClick={() => { handleDeleteImage(currentImage) }}
                                                    >
                                                        <Image src={DeleteImage} boxSize={'28px'} />
                                                    </Center>
                                                </Box>
                                                <Box {...styles.rightControls}>
                                                    <Center bg={"#5F4DC7"} p={2} cursor={"pointer"} onClick={handleAddImage}>
                                                        <RxPlus size={"28px"} color='#FFFFFF' />
                                                    </Center>
                                                </Box>
                                            </React.Fragment>
                                        )}
                                    </Box>
                                    {/* Replace the Box with edit controls with this code */}
                                    <Box position="relative" border={"1px solid #00000029"} p={'18px'}
                                        height={["240px", "400px"]} overflow="hidden">
                                        {isEditMode &&
                                            <Box position="absolute" bottom="0px" left="0px" zIndex={10}>
                                                <Center
                                                    bg="#F5F5F5" p={2}
                                                    cursor={"pointer"}
                                                    onClick={() => { setOpenMath(true) }}
                                                    boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
                                                    borderRadius="4px"
                                                >
                                                    <RiFormula size={"28px"} color='#5F4DC7' />
                                                </Center>
                                            </Box>
                                        }
                                        {/* The edit button that stays fixed when scrolling */}
                                        {!isEmptyOrNull(currentText) &&
                                            <Box position="absolute" bottom="0px" right="0px" zIndex={10}>
                                                <Center
                                                    bg="#F5F5F5" p={2}
                                                    cursor={"pointer"}
                                                    onClick={handleUpdateText}
                                                    boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
                                                    borderRadius="4px"
                                                >
                                                    {isEditMode ?
                                                        <IoMdCheckmark size={"28px"} color='#5F4DC7' />
                                                        :
                                                        <CiEdit size={"28px"} color='#5F4DC7' />
                                                    }
                                                </Center>
                                            </Box>
                                        }
                                        {renderStatusContent()}
                                        {!isEmptyOrNull(currentText) && !isEditMode && (
                                            <Box w="100%" h="100%" overflowY="auto">
                                                {isStandalone ?
                                                    <KatexRender math={currentText || ""} />
                                                    :
                                                    <LatexRenderView math={currentText || ""} />
                                                }
                                            </Box>
                                        )
                                        }
                                        {isEditMode &&
                                            <Box w="100%" h="100%" overflowY="auto" overflowX={"auto"}>
                                                <StudentEditor
                                                    data={inputvalue}
                                                    handleChange={setInputValue}
                                                    icons={{}}
                                                    height="100%"
                                                    handleOpenMath={openMath}
                                                    handleCloseMath={() => {
                                                        setOpenMath(false);
                                                    }}
                                                    showBottomBar={false}
                                                />
                                            </Box>
                                        }
                                    </Box>
                                </Grid>
                            ) : (
                                <Center {...styles.fileUploadCenter}>
                                    <Image src={Upload} boxSize={'64px'} cursor={"pointer"} onClick={handleAddImage} />
                                    <Text {...styles.fileUploadText}>{`Select files to upload`}</Text>
                                    <Text {...styles.uploadLimitText}>{`JPG, PNG (max ${window?.ENV?.OCR_UPLOAD_SIZE_LIMIT} MB)`}</Text>
                                </Center>
                            )}
                        </Box>
                    </ModalBody>

                    <ModalFooter {...styles.modalFooter}>
                        {bottomButtons()}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </React.Fragment>
    )
}
